import * as React from 'react';
import { Component, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { reportUxError, trackErrorRetry, ERROR_TYPES } from '@/lib/analytics/error-telemetry';
import { sfIcons } from '@/lib/sf-icons';
import { SFIcon } from '@/components/ui/sf-icon';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'page' | 'section' | 'component';
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorId?: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryCount = 0;
  private readonly maxRetries = 3;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { 
      hasError: true, 
      error,
      errorId: Math.random().toString(36).substring(2, 15)
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { level = 'component', onError } = this.props;
    const { errorId } = this.state;

    // Determine surface from level
    const surface = level === 'page' ? 'page' : level === 'section' ? 'section' : 'component';

    // Track error using centralized helper (always critical for error boundaries)
    reportUxError(error, {
      type: ERROR_TYPES.ERROR_BOUNDARY,
      surface,
      critical: true, // Error boundaries are always critical
      metadata: {
        level,
        errorId,
        errorMessage: error.message,
        componentStack: errorInfo.componentStack.split('\n')[1] || 'unknown',
        retryCount: this.retryCount,
      },
    });

    // Log to console
    console.error(`ErrorBoundary (${level}):`, {
      error,
      errorInfo,
      errorId,
      retryCount: this.retryCount
    });

    // Call custom error handler if provided
    onError?.(error, errorInfo);
  }

  handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      
      // Track retry using centralized helper
      const { level = 'component' } = this.props;
      const surface = level === 'page' ? 'page' : level === 'section' ? 'section' : 'component';
      trackErrorRetry(ERROR_TYPES.ERROR_BOUNDARY, surface);
      
      this.setState({ hasError: false, error: undefined, errorId: undefined });
    } else {
      // Force page reload if too many retries (with SSR guard)
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    }
  };

  handleReport = () => {
    const { error, errorId } = this.state;
    // Note: Report action is not tracked as UX event (it's a support action)
    // Could be tracked separately if needed
    
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      const subject = `Error Report - ${errorId}`;
      const body = `Error ID: ${errorId}
Error: ${error?.message}
Stack: ${error?.stack}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
Timestamp: ${new Date().toISOString()}`;
      
      const mailtoUrl = `mailto:support@company.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoUrl, '_blank', 'noopener,noreferrer');
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { level = 'component' } = this.props;
      const { error, errorId } = this.state;
      
      const isPageLevel = level === 'page';
      const canRetry = this.retryCount < this.maxRetries;
      const ContainerTag = isPageLevel ? 'section' : 'div';

      return (
        <ContainerTag 
          role={isPageLevel ? "alert" : undefined}
          aria-live={isPageLevel ? "assertive" : "polite"}
          data-testid="error-boundary"
        >
          <Card className={`border-destructive ${isPageLevel ? 'mx-auto max-w-lg mt-8' : ''}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <SFIcon icon={sfIcons.alertTriangle} className="h-5 w-5" />
              {isPageLevel ? 'Page Error' : 'Section Unavailable'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              {isPageLevel 
                ? 'The page encountered an error and cannot be displayed.' 
                : "We couldn't load this section."
              } {canRetry ? 'You can try again or refresh the page.' : 'Please refresh the page.'}
            </p>
            
            {error && (
              <details className="text-sm text-muted-foreground">
                <summary className="cursor-pointer flex items-center gap-2">
                  <SFIcon icon={sfIcons.bug} className="h-4 w-4" />
                  Error details (ID: {errorId})
                </summary>
                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto max-h-32">
                  {error.message || error.name || 'An unexpected error occurred (no message available)'}
                </pre>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={this.handleRetry}
                className="flex-1"
                disabled={!canRetry}
              >
                <SFIcon icon={sfIcons.refresh} className="h-4 w-4 mr-2" />
                {canRetry ? `Retry (${this.maxRetries - this.retryCount} left)` : 'Refresh Page'}
              </Button>
              
              <Button
                variant="outline"
                onClick={this.handleReport}
                className="flex-1"
              >
                <SFIcon icon={sfIcons.mail} className="h-4 w-4 mr-2" />
                Report Issue
              </Button>
            </div>
          </CardContent>
        </Card>
        </ContainerTag>
      );
    }

    return this.props.children;
  }
}

// Convenience wrapper for page-level errors
export function PageErrorBoundary({ children, onError }: Omit<ErrorBoundaryProps, 'level'>) {
  return (
    <ErrorBoundary level="page" onError={onError}>
      {children}
    </ErrorBoundary>
  );
}

// Convenience wrapper for section-level errors
export function SectionErrorBoundary({ children, onError }: Omit<ErrorBoundaryProps, 'level'>) {
  return (
    <ErrorBoundary level="section" onError={onError}>
      {children}
    </ErrorBoundary>
  );
}